// @ts-nocheck
import React from 'react';
import { momentTz } from '../dates';

import isArray from 'lodash/isArray';
import { Box, Chip } from '@mui/material';

import { isValueEmpty, isJsonString, roundToDecimals } from '../core';

import { getLabelPathFromLabelConfig } from '../labels';
import {
  DD_MM_YYYY_DATE_FORMAT,
  DATE_FORMAT,
  YYYY_MM_DD_DATE_FORMAT,
  DATE_FORMAT_HH_MM_SS,
  USERS_WITH_DRAFT,
  LINKED_ENTITIES,
  NOOP,
  EMPTY_ARRAY,
} from '../../const/CommonConst';
import EntityHealthIndicator from '../../components/Common/ModelInventoryAndAssociation/EntityHealthIndicator';
import ColumnHeaderWithFilter from '../../components/Common/ModelInventoryAndAssociation/ColumnHeaderWithFilter';
import HealthColumnHeader from '../../components/Common/Tables/Headers/HealthColumnHeader';
import AlertHealthIndicator from '../../components/Common/ModelInventoryAndAssociation/AlertHealthIndicator';
import LinkedEntitiesCell from '../../components/Common/ModelInventoryAndAssociation/LinkedEntitiesCell';
import { isDateValid } from '../attributes';

const FIELD_LABELS = {
  type: { label: 'Template Name', fullWordMatch: true },
  association_name: { label: 'Artifact Name', fullWordMatch: true },
  association_id: { label: 'Association ID', fullWordMatch: true },
  associated_id: { label: 'Associated ID', fullWordMatch: true },
  created_at: { label: 'Created At', fullWordMatch: true },
  updated_at: { label: 'Updated At', fullWordMatch: true },
  updated_by: { label: 'Updated By', fullWordMatch: true },
  created_by: { label: 'Created By', fullWordMatch: true },
  unique_id: { label: 'Unique ID', fullWordMatch: true },
  due_date: { label: 'Due Date', fullWordMatch: true },
  request_type: { label: 'Request Type', fullWordMatch: true },
  entity_name: { label: 'Entity Name', fullWordMatch: true },
  entity_alias: { label: 'Entity Alias', fullWordMatch: true },
  entity_type_name: { label: 'Entity Type', fullWordMatch: true },
};

const FORMAT = [DD_MM_YYYY_DATE_FORMAT, DATE_FORMAT, YYYY_MM_DD_DATE_FORMAT];

// Helper function to format date
export const formatDate = (date, showTimeWithSeconds = false) => {
  // check if date include time or not
  const hasTime = date.includes('T') || date.includes('Z');

  // convert into date and time format if showTimeWithSeconds is true and hasTime is true
  // else use default Date format
  if (showTimeWithSeconds && hasTime) {
    return momentTz.utc(date).local().format(DATE_FORMAT_HH_MM_SS);
  }
  return momentTz(date, FORMAT).format(DATE_FORMAT);
};
/**
 * Replaces the specified keys in the given item with their corresponding labels based on the provided fieldLabels configuration.
 *
 * The function iterates through the keys in `fieldLabels` and uses regular expressions to replace
 * each key in the `item` with the corresponding `label`. It can be configured to either match whole words
 * or allow partial matches using the `fullWordMatch` flag.
 *
 * @param {string} item - The string item in which the replacements need to be made.
 * @param {Object} fieldLabels - An object where each key is the string to be replaced and its value is an object containing:
 * @param {string} fieldLabels.label - The label to replace the key with.
 * @param {boolean} fieldLabels.fullWordMatch - A boolean indicating whether the key should be replaced only when it matches the whole word.
 *
 * @returns {string} - The transformed string item with replacements made based on `fieldLabels`.
 *
 * @example
 * // Define the fieldLabels configuration
 * const fieldLabels = {
 *   type: { label: 'Template Name', fullWordMatch: true },
 *   association: { label: 'ARTIFACT', fullWordMatch: false }
 * };
 *
 * // Example item string
 * const item = "This type is associated with the association system.";
 *
 * // Replace using the fieldLabels configuration
 * const result = replaceLabel(item, fieldLabels);
 * console.log(result); // Output: "This Template Name is associated with the ARTIFACT system."
 *
 * @example
 * // Example with partial word matching
 * const fieldLabels = {
 *   entity: { label: 'Object', fullWordMatch: false },
 * };
 * const item = "entity_id, entity_code, entity_alias";
 * const result = replaceLabel(item, fieldLabels);
 * console.log(result); // Output: "Object_id, Object_code, Object_alias"
 */
const replaceLabel = (item, fieldLabels) => {
  // If the item is not added in the fieldsLabel object then we will not replace it
  if (!fieldLabels[item]) {
    return item;
  }
  Object.keys(fieldLabels).forEach((key) => {
    const { label, fullWordMatch } = fieldLabels[key];
    const regex = new RegExp(fullWordMatch ? `\\b${key}\\b` : key, 'gi');
    // eslint-disable-next-line no-param-reassign
    item = item.replace(regex, label);
  });
  return item;
};

const getDynamicFieldsDependsOnEntityType = () => {
  return {
    model_id: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelInventory`,
      'featureLabel',
      'Model'
    )} ID`,

    model_name: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelInventory`,
      'featureLabel',
      'Model'
    )} Name`,

    association_id: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelAssociation`,
      'featureLabel',
      'Model'
    )} ID`,

    association_name: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelAssociation`,
      'featureLabel',
      'Model'
    )} Name`,
    created_at: 'Created On',
    updated_at: 'Updated On',
  };
};

/**
 * Returns a dynamic heading for a given item based on the entity type.
 *
 * @param {string} item - The item to generate a heading for.
 * @param {string} entityType - The type of entity (e.g. 'ModelInventory', 'ModelAssociation').
 * @returns {string} The dynamic heading for the given item.
 * @description This function checks if the item has a dynamic field dependency based on the entity type.
 *              If not, it returns a formatted label for the item.
 */
const getDynamicHeading = (item, entityType) => {
  const DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE =
    getDynamicFieldsDependsOnEntityType();
  if (DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE[item]) {
    return DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE[item];
  }
  if (item === 'Health' || item === 'entity_health') {
    if (entityType === 'ModelInventory') {
      return getLabelPathFromLabelConfig(
        `LABELS?.modelInventory?.table?.column?.ModelHealth`,
        'featureLabel',
        'Model Health'
      );
    }
    if (entityType === 'ModelAssociation') {
      return getLabelPathFromLabelConfig(
        `LABELS?.modelAssociation?.table?.column?.ArtifactHealth`,
        'label',
        `${getLabelPathFromLabelConfig(
          `TEMPLATE_TYPES.ModelAssociation`,
          'featureLabel',
          'Model'
        )} Health`
      );
    }
  }
  return replaceLabel(item, FIELD_LABELS); // Convert item to a more readable label format and make also in uppercase (e.g., "due_date" -> "DUE DATE") ,
};

/**
 * @function createDynamicColumns
 * @description function calculate calculate dynamic columns for table and return array of columns
 * @param {Array<String>} data -array of colums names
 * @param {number} width -minimum width of column
 *  @param {function} HeaderCell - type of header
 * @returns  {Array} array of dynamic columns
 */
export const createDynamicColumns = ({
  data,
  width,
  HeaderCell,
  excludeCols = EMPTY_ARRAY,
  disabledOrderingCols = EMPTY_ARRAY,
  entityType = null,
  handleOpenFilter = NOOP,
  enableHeaderFilterIcon = false,
  showTimeWithSeconds = false,
}) => {
  // Helper function to get column width
  const getColumnWidth = (columnId) => {
    if (columnId === 'Health' || columnId === 'entity_health') {
      return {
        width: 200,
      };
    }
    return width ? { width } : {};
  };

  // Helper function to get entity name based on entity type
  const getEntityName = (row) => {
    if (row?.original?.entity_alias) {
      return row?.original?.entity_alias;
    }
    if (row?.original?.entity_type === 'ModelInventory') {
      return row?.original?.model_name;
    }
    return row?.original?.association_name;
  };

  /**
   * Returns the accessor value used by ReactMuiTableV8 for rendering and the
   * native table cell title. Date values are formatted here because the table
   * sets the <td> title from the accessor value before the custom Cell output.
   *
   * @param {object} rowData - Source row object.
   * @param {string} columnName - Column key to read from the row.
   * @param {string} dataType - Attribute data type for the column.
   * @returns {*} Formatted date value for Date columns, otherwise raw cell value.
   */
  const getAccessorValue = (rowData, columnName, dataType) => {
    const cellValue = rowData?.[columnName];

    if (dataType === 'Date' && isDateValid(cellValue)) {
      return formatDate(cellValue, showTimeWithSeconds);
    }

    return cellValue;
  };

  const renderHealthCell = (columnName, row) => {
    if (row?.original?.health_status?.label) {
      return (
        <EntityHealthIndicator
          health={row?.original?.health_status}
          iconSize="18px"
        />
      );
    }
    return (
      <AlertHealthIndicator
        flag={row?.original?.[columnName]}
        iconSize="18px"
      />
    );
  };

  const getColumnHeader = (props) => {
    const columnId = props?.column?.id;
    const columnLabel = props?.column?.heading;
    const isAssoctionDataType = props?.column?.dataType === 'Association';
    const headerContent =
      columnId === 'Health' || columnId === 'entity_health'
        ? HealthColumnHeader(props)
        : HeaderCell(props);

    if (
      enableHeaderFilterIcon &&
      columnId !== 'Health' &&
      columnId !== 'entity_health' &&
      !isAssoctionDataType
    ) {
      return (
        <ColumnHeaderWithFilter
          columnLabel={columnLabel}
          onFilterClick={handleOpenFilter}
        >
          {headerContent}
        </ColumnHeaderWithFilter>
      );
    }
    return headerContent;
  };

  const renderCell = ({ row, column }) => {
    const item = column?.id;

    const cellValue = row?.original?.[item];
    let parsedCellValue = cellValue;

    if (typeof cellValue === 'string') {
      try {
        parsedCellValue = JSON.parse(cellValue);
      } catch {
        parsedCellValue = cellValue;
      }
    }
    if (column?.dataType === 'Association') {
      let entityData = {};

      if (typeof cellValue === 'string') {
        if (isJsonString(cellValue)) {
          entityData = JSON.parse(cellValue || '{}');
        } else {
          return cellValue;
        }
      } else {
        entityData = cellValue;
      }
      return isValueEmpty(entityData?.entity_id || entityData?.unique_id)
        ? '-'
        : `${entityData?.unique_id || entityData?.entity_id}`;
    }

    if (column?.dataType === 'User') {
      if (
        column?.input_control_type === 'MULTI_SELECT' &&
        isArray(parsedCellValue)
      ) {
        return parsedCellValue.join(', ') || '-';
      }

      return parsedCellValue || '-';
    }

    if (column?.dataType === 'Group') {
      if (
        column?.input_control_type === 'MULTI_SELECT' &&
        isArray(parsedCellValue)
      ) {
        return parsedCellValue.join(', ') || '-';
      }

      return parsedCellValue || '-';
    }

    if (item === 'Health' || item === 'entity_health') {
      return renderHealthCell(item, row);
    }

    if (item === USERS_WITH_DRAFT) {
      return cellValue === 'True' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            cursor: 'pointer',
          }}
        >
          <Chip
            label="Unsaved Changes"
            title="Unsaved Changes"
            size="small"
            className="sm-chip md-chip"
            sx={{
              minHeight: '22px',
              fontSize: '12px',
              fontWeight: '600',
            }}
            color="primary"
          />
        </Box>
      ) : (
        '-'
      );
    }

    if (item === 'template_version_number') {
      if (isValueEmpty(cellValue)) return '-';
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            cursor: 'pointer',
          }}
        >
          <Chip
            label={String(cellValue)}
            title={String(cellValue)}
            size="small"
            className="sm-chip"
            sx={{
              minHeight: '22px',
              fontSize: '12px',
              fontWeight: '600',
            }}
            color="primary"
          />
        </Box>
      );
    }

    if (item === LINKED_ENTITIES) {
      return cellValue ? (
        <LinkedEntitiesCell
          value={cellValue}
          row={row}
          entityType={entityType}
        />
      ) : (
        '-'
      );
    }

    if (item === 'model_name' || item === 'association_name') {
      return getEntityName(row);
    }

    if (isArray(parsedCellValue)) {
      return (
        parsedCellValue
          ?.map((attr) => {
            if (typeof attr === 'string') {
              return attr;
            }

            return attr?.label || attr?.value || '-';
          })
          ?.join(', ') || '-'
      );
    }
    // If the value is in date format, it will be converted into valid format
    if (isDateValid(cellValue)) {
      return formatDate(cellValue, showTimeWithSeconds);
    }

    if (typeof cellValue === 'number') {
      return isValueEmpty(cellValue) ? '-' : roundToDecimals(cellValue, 3);
    }

    // If the value is boolean, it will be converted to string as react dose not render boolean values
    if (typeof cellValue === 'boolean') {
      return cellValue.toString();
    }

    if (
      typeof cellValue === 'object' &&
      cellValue?.color &&
      !isValueEmpty(cellValue?.value)
    ) {
      return (
        <Box
          sx={{
            textAlign: 'left',
            bgcolor: cellValue.color,
          }}
        >
          {cellValue.value}
        </Box>
      );
    }

    // Here, if the cell value is an object or empty, we return 'N/A', otherwise we return the cell value itself. Some of the attributes values are not supported to show in table directly so we are returning N/A for those.
    return isValueEmpty(parsedCellValue) || typeof parsedCellValue === 'object'
      ? '-'
      : parsedCellValue;
  };

  return data
    ?.filter((item) => {
      /**
       * we are receiving two types of data here
       * 1. array of objects: [{name: ColumnName, label: ColumnLabel}, ...]
       * 2. array of strings: [ColumnName, ColumnName, ...];
       */
      const colName = typeof item === 'string' ? item : item?.field;
      return !excludeCols?.includes(colName);
    })
    ?.map((item, index) => {
      /**
       * we are receiving two types of data here
       * 1. array of objects: [{name: ColumnName, label: ColumnLabel}, ...]
       * 2. array of strings: [ColumnName, ColumnName, ...];
       */
      const colName = typeof item === 'string' ? item : item?.name;
      const columnDataType = typeof item === 'string' ? item : item?.dataType;
      const colLabel =
        typeof item === 'string'
          ? getDynamicHeading(item, entityType)
          : item?.label;

      return {
        Header: getColumnHeader,
        id: colName,
        dataType: columnDataType,
        input_control_type: item?.input_control_type,
        accessor: (value) => {
          if (colName === 'model_id' || colName === 'association_id') {
            return (
              value?.entity_code ||
              (value?.entity_type === 'ModelInventory'
                ? value?.model_id
                : value?.association_id)
            );
          }

          if (colName === 'model_name' || colName === 'association_name') {
            return (
              value?.entity_alias ||
              (value?.entity_type === 'ModelInventory'
                ? value?.model_name
                : value?.association_name)
            );
          }

          if (colName === 'Health' || colName === 'entity_health') {
            return value?.health_status?.label;
          }
          return getAccessorValue(value, colName, columnDataType);
        },

        Cell: renderCell,
        ...(width ? getColumnWidth(colName) : {}),
        heading: colLabel,
        disableOrdering: disabledOrderingCols.includes(colName),
        index,
        disableSortBy: enableHeaderFilterIcon,
      };
    });
};

const createModelFieldObject = (field, suffix, dataType, operators) => {
  return {
    name: field,
    label: `${getLabelPathFromLabelConfig(
      'TEMPLATE_TYPES?.ModelInventory',
      'featureLabel',
      'Model'
    )} ${suffix}`,
    datatype: dataType,
    operators,
  };
};

export const createDynamicFilters = (data) => {
  return data.map((value) => {
    const dataType = value?.data_type;
    let operators;

    if (dataType === 'Character') {
      if (value?.field_type === 'MULTI_SELECT') {
        operators = [
          { name: '!=', label: '!=' },
          { name: '=', label: '=' },
          { name: 'in', label: 'In' },
          { name: 'not in', label: 'Not in' },
        ];
      } else {
        operators = [
          { name: '!=', label: '!=' },
          { name: '=', label: '=' },
        ];
      }
    } else if (dataType === 'Date') {
      operators = [
        { name: '<', label: '<' },
        { name: '>', label: '>' },
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    } else if (dataType === 'Integer' || dataType === 'Decimal') {
      operators = [
        { name: '>=', label: '>=' },
        { name: '<=', label: '<=' },
        { name: '<', label: '<' },
        { name: '>', label: '>' },
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    } else {
      operators = [
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    }

    if (value?.name === 'model_id' || value?.name === 'model_name') {
      const suffix = value?.name === 'model_id' ? 'id' : 'name';
      return createModelFieldObject(value?.name, suffix, dataType, operators);
    }

    // if label doesn't exist, pass field
    const rawLabel = value?.label ?? replaceLabel(value?.field, FIELD_LABELS);
    // Create label with capitalized first letter
    const label = rawLabel?.charAt(0)?.toUpperCase() + rawLabel?.slice(1);
    return {
      name: value?.field,
      label,
      datatype: dataType,
      operators,
    };
  });
};

export const getTableColumns = (allAttributes) => {
  // some data types are not supported in table yet, so excluding them
  const excludedDataType = new Set([
    'Table',
    'AdvancedTable',
    'Checklist',
    'Paragraph',
    'Document',
    'Chart',
    'HTML',
    'IssueType',
    'InventoryView',
    'TemplateList',
  ]);

  // here we will return an array of object: {name and label}
  return allAttributes
    .filter((item) => !excludedDataType.has(item.data_type))
    .map((item) => ({
      name: item?.name,
      label: item?.label,
      dataType: item?.data_type,
      input_control_type: item?.field_type,
    }));
};

export const blockInvalidNumbers = (e, type) => {
  if (type === 'Integer') {
    return ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault();
  }
  return ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();
};

/**
 * @description Changes the table page index based on the current data, page size, using provided function to go to a page.
 * @param {number} pageIndex - The current page index.
 * @param {Array} data - The data displayed in the table.
 * @param {number} pageSize - The number of items displayed per page.
 * @param {Function} gotoPage - The function to navigate to a specific page.
 */
export const changeTablePageIndex = (pageIndex, data, pageSize, gotoPage) => {
  // If we have items equal to page size on the page, we will not going to change the page
  if (data && data.length % pageSize === 0) {
    // Check if the current page index is less than or equal to 0.
    if (pageIndex - 1 <= 0) {
      // If so, go to the first page (index 0).
      gotoPage(0);
    } else {
      // Otherwise, go to the previous page (decrement pageIndex by 1).
      gotoPage(pageIndex - 1);
    }
  }
};

export const formatTimeString = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return '';

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ');
};
